import type { FieldMappingDto } from '@app/src/types/tabularImport'
import { Box, Button, Divider, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'

type MappingOverridePopoverProps = {
  anchorEl: HTMLElement | null
  open: boolean
  mapping: FieldMappingDto
  fieldOptions: string[]
  mappedTargetFields: string[]
  sourceColumns: string[]
  isUnassigned: boolean
  isRequired: boolean
  unassignedHint: string
  extras?: React.ReactNode
  onClose: () => void
  onRetarget: (targetField: string) => void
  onSplit: () => void
  onToggleApplied: () => void
}

const MappingOverridePopover: React.FC<MappingOverridePopoverProps> = ({
  anchorEl,
  open,
  mapping,
  fieldOptions,
  mappedTargetFields,
  sourceColumns,
  isUnassigned,
  isRequired,
  unassignedHint,
  extras,
  onClose,
  onRetarget,
  onSplit,
  onToggleApplied,
}) => {
  const sourceColumn = sourceColumns[0] ?? mapping.targetFieldName
  const isMultiColumn = sourceColumns.length > 1
  // When unassigned (a column split out of a merge or pulled back from a category), its
  // target name is the raw header, not a real entity field — nothing to pre-select.
  const [field, setField] = useState(isUnassigned ? '' : mapping.targetFieldName)
  const movingToMappedField = field !== mapping.targetFieldName && mappedTargetFields.includes(field)

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, width: 320 }}>
        <Typography variant="subtitle2">
          {isUnassigned ? (
            <>Column &ldquo;{sourceColumn}&rdquo;</>
          ) : (
            <>
              Column &ldquo;{sourceColumn}&rdquo; &rarr; {mapping.targetFieldName}
            </>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {isUnassigned ? unassignedHint : "The AI guessed this mapping. Change it below if it's wrong."}
        </Typography>
        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Map to a different field
            </Typography>
            <Box display="flex" gap={1} mt={0.5}>
              <TextField select size="medium" fullWidth value={field} onChange={e => setField(e.target.value)}>
                {isUnassigned && (
                  <MenuItem value="" disabled>
                    Choose a field…
                  </MenuItem>
                )}
                {fieldOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                size="small"
                disabled={!field || field === mapping.targetFieldName}
                onClick={() => onRetarget(field)}
              >
                Move
              </Button>
            </Box>
            {movingToMappedField && (
              <Typography variant="caption" color="warning.main">
                Replaces the current mapping for {field}.
              </Typography>
            )}
          </Box>

          {isMultiColumn ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Split into separate columns
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Merges {sourceColumns.length} columns: {sourceColumns.join(', ')}.
              </Typography>
              <Button variant="outlined" size="small" sx={{ mt: 0.5 }} onClick={onSplit}>
                Split
              </Button>
            </Box>
          ) : (
            extras
          )}
        </Stack>

        {/* Required fields must stay applied or the import can't proceed, so they can be
            remapped but never disabled. A disabled column can be turned back on. */}
        {mapping.isApplied && !isRequired && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Button size="small" color="error" onClick={onToggleApplied}>
              Disable this column
            </Button>
          </>
        )}
        {!mapping.isApplied && !isUnassigned && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Button size="small" onClick={onToggleApplied}>
              Enable this column
            </Button>
          </>
        )}
      </Box>
    </Popover>
  )
}

export default MappingOverridePopover
